import {
    Features,
    getFeatureFlagValue,
} from '@experiences/feature-flags';
import React from 'react';

import { routePaths } from '../../common/constants/routePaths';
import type { UiRouteObject } from '../../container/routeConfigs/UiRouteObject';
import GovernanceV2IndexRedirect from './GovernanceV2IndexRedirect';
import GovernanceV2OrgAdminGuard from './GovernanceV2OrgAdminGuard';
import GovernanceV2PageComponent from './GovernanceV2PageComponent';

const GovernancePolicyLibraryPageComponent = React.lazy(
    () => import('./subcomponents/policyLibrary/GovernancePolicyLibraryPageComponent'),
);
const GovernancePolicyDeploymentPageComponent = React.lazy(
    () => import('./subcomponents/policyDeployment/GovernancePolicyDeploymentPageComponent'),
);
const GovernanceComplianceStandardsPageComponent = React.lazy(
    () => import('./subcomponents/policyPacks/GovernanceComplianceStandardsPageComponent'),
);
const GovernanceComplianceStandardDetailPageComponent = React.lazy(
    () => import('./subcomponents/policyPacks/GovernanceComplianceStandardDetailPageComponent'),
);
const CodedAppIframe = React.lazy(
    () => import('./subcomponents/dashboards/CodedAppIframe'),
);
const CreateEditProductPolicyPage = React.lazy(
    () => import('./subcomponents/policyLibrary/CreateEditProductPolicyPage'),
);
const CreateEditAccessPolicyPage = React.lazy(
    () => import('./subcomponents/policyLibrary/CreateEditAccessPolicyPage'),
);
const GovernanceDefaultPageComponent = React.lazy(
    () => import('./subcomponents/defaultPage/GovernanceDefaultPageComponent'),
);
const GovernanceNotificationsPageComponent = React.lazy(
    () => import('./subcomponents/notifications/GovernanceNotificationsPageComponent'),
);
const GovernanceLlmAuditPageComponent = React.lazy(
    () => import('./subcomponents/llmAudit/GovernanceLlmAuditPageComponent'),
);
const GovernanceComplianceViolationsPageComponent = React.lazy(
    () => import('./subcomponents/dashboards/complianceViolations/GovernanceComplianceViolationsPageComponent'),
);
const GovernanceComplianceViolationsTracesPageComponent = React.lazy(
    () => import('./subcomponents/dashboards/complianceViolations/GovernanceComplianceViolationsTracesPageComponent'),
);
const GovernanceTracesPageComponent = React.lazy(
    () => import('./subcomponents/observability/GovernanceTracesPageComponent'),
);

// Read once at module load (env flags are injected before the bundle runs); a runtime flip needs a reload.
const isNewCreateEditPolicyFlowEnabled = getFeatureFlagValue(Features.EnableGovernanceV2NewCreateEditPolicyFlow.name);
const isComplianceTrackerEnabled = getFeatureFlagValue(Features.EnableGovernanceComplianceTracker.name);
// Compliance Violations dashboard is gated per-org; when off, its routes are not registered.
const isComplianceViolationsDashboardEnabled = getFeatureFlagValue(
    Features.EnableGovernanceV2ComplianceViolationsDashboard.name,
);

export const GovernanceV2Routes: UiRouteObject = {
    path: routePaths.governance,
    element: <GovernanceV2PageComponent />,
    children: [
        {
            index: true,
            element: <GovernanceV2IndexRedirect />,
        },
        // Policy section is org-admin only; one guard covers every nested policy route.
        {
            element: <GovernanceV2OrgAdminGuard />,
            children: [
                {
                    path: routePaths.governancePolicyLibrary,
                    element: <GovernancePolicyLibraryPageComponent />,
                },
                // In-portal create/edit routes are gated; when off, the legacy governance_ flow handles create/edit.
                ...(isNewCreateEditPolicyFlowEnabled ? [
                    {
                        path: routePaths.governanceProductPolicyCreate,
                        element: <CreateEditProductPolicyPage />,
                    },
                    {
                        path: routePaths.governanceProductPolicyEdit,
                        element: <CreateEditProductPolicyPage />,
                    },
                    {
                        path: routePaths.governanceAccessPolicyCreate,
                        element: <CreateEditAccessPolicyPage />,
                    },
                    {
                        path: routePaths.governanceAccessPolicyEdit,
                        element: <CreateEditAccessPolicyPage />,
                    },
                ] : []),
                {
                    path: routePaths.governancePolicyDeployment,
                    element: <GovernancePolicyDeploymentPageComponent />,
                },
                {
                    path: routePaths.governanceComplianceStandards,
                    element: <GovernanceComplianceStandardsPageComponent />,
                },
                {
                    path: routePaths.governanceComplianceStandardDetail,
                    element: <GovernanceComplianceStandardDetailPageComponent />,
                },
                ...(isComplianceTrackerEnabled ? [
                    {
                        path: routePaths.governanceNotifications,
                        element: <GovernanceNotificationsPageComponent />,
                    },
                ] : []),
                {
                    path: routePaths.governanceObservabilityTraces,
                    element: <GovernanceTracesPageComponent />,
                },
                ...(isComplianceViolationsDashboardEnabled ? [
                    {
                        path: routePaths.governanceComplianceViolations,
                        element: <GovernanceComplianceViolationsPageComponent />,
                    },
                    {
                        path: routePaths.governanceComplianceViolationsTraces,
                        element: <GovernanceComplianceViolationsTracesPageComponent />,
                    },
                ] : []),
            ],
        },
        {
            path: routePaths.governanceDashboard,
            element: <CodedAppIframe />,
        },
        {
            path: routePaths.governanceGettingStarted,
            element: <GovernanceDefaultPageComponent />,
        },
        // LLM audit is tenant-scoped (data scope enforced server-side from the JWT), so it stays
        // outside the org-admin guard.
        {
            path: routePaths.governanceLlmAudit,
            element: <GovernanceLlmAuditPageComponent />,
        },
    ],
};
